import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Typography,
  Tooltip,
  Popover,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Backdrop,
  Portal,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import useFetchPhoto from "../../hooks/useFetchPhoto";
import { getDescricaoByCodigo } from "../../enums/Roles";
import { useAppDispatch } from "../..";
import * as loginActions from "../../store/actions/login";
import { UserDataTypesDependente } from "../../store/actions/login";
import { getCallbackPathFromRole } from "../../helpers/HelperFunctions";
import { getCallbackPath } from "../../helpers/SidebarMenuHelperFunctions";
import { mountUserDataPostLoginDependentes } from "../../helpers/LoginHelperFunctions";
import { Paths } from "../../enums/Paths";
import GenericModal from "../genericModal/GenericModal";
import EmailModal from "../emailModal/EmailModal";

interface IUserProfileProps {
  isCollapsed?: boolean;
}

const UserProfile = ({ isCollapsed }: IUserProfileProps) => {
  const {
    userInfo,
    user,
    photo: reduxPhoto,
    currentProfile,
    currentCompany,
    userCompanies,
    dependentes,
    currentDependente,
  }: any = useSelector((state: any) => state.login);

  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  
  // Anchor states for separate Popovers
  const [companyAnchorEl, setCompanyAnchorEl] = useState<null | HTMLElement>(null);
  const [roleAnchorEl, setRoleAnchorEl] = useState<null | HTMLElement>(null);
  const [dependenteAnchorEl, setDependenteAnchorEl] = useState<null | HTMLElement>(null);

  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyToChange, setCompanyToChange] = useState<any>();
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleToChange, setRoleToChange] = useState<any>();
  const [showDependenteModal, setShowDependenteModal] = useState(false);
  const [dependenteToChange, setDependenteToChange] = useState<any>();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [cpfDependenteAtual, setcpfDependenteAtual] = useState("");

  const [isLoadingDependente, setIsLoadingDependente] = useState(false);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(false);

  // Step-by-step Onboarding Tour state
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState<number>(1);
  const [tutorialAnchorEl, setTutorialAnchorEl] = useState<null | HTMLElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const userHeaderRef = useRef<HTMLDivElement | null>(null);
  const companyBoxRef = useRef<HTMLDivElement | null>(null);
  const roleBoxRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const photoFileName = userInfo?.foto || reduxPhoto || null;
  useFetchPhoto(photoFileName, setPhotoUrl);

  const formatFirstAndLastName = (nameStr: string) => {
    if (!nameStr) return "Usuário";
    const parts = nameStr.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
    }
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];

    const capitalize = (str: string) =>
      str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    return `${capitalize(firstName)} ${capitalize(lastName)}`;
  };

  // Prioritize full name (userInfo.nome or userInfo.nomeCompleto) to extract first + last name
  const rawFullName =
    userInfo?.nome ||
    userInfo?.nomeCompleto ||
    userInfo?.comoSerChamado ||
    user ||
    "Usuário";
  const displayName = formatFirstAndLastName(rawFullName);

  const currentRoleDesc = currentProfile
    ? getDescricaoByCodigo(currentProfile) || currentProfile
    : "Selecione o perfil";

  const hasDependentes =
    dependentes &&
    dependentes.length > 0 &&
    dependentes.some((d: any) => !d.responsavel);

  const totalSteps = hasDependentes ? 4 : 3;

  const updateTutorialAnchor = (step: number) => {
    if (step === 1) {
      setTutorialAnchorEl(userHeaderRef.current);
    } else if (step === 2) {
      setTutorialAnchorEl(companyBoxRef.current || userHeaderRef.current);
    } else if (step === 3) {
      setTutorialAnchorEl(roleBoxRef.current || userHeaderRef.current);
    } else if (step === 4) {
      setTutorialAnchorEl(userHeaderRef.current);
    }
  };

  const updateRect = useCallback(() => {
    if (tutorialAnchorEl) {
      setRect(tutorialAnchorEl.getBoundingClientRect());
    }
  }, [tutorialAnchorEl]);

  useEffect(() => {
    if (showTutorial && tutorialAnchorEl) {
      updateRect();
      const timer = setTimeout(updateRect, 60);
      window.addEventListener("resize", updateRect);
      window.addEventListener("scroll", updateRect, true);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", updateRect);
        window.removeEventListener("scroll", updateRect, true);
      };
    }
  }, [showTutorial, tutorialAnchorEl, updateRect]);

  useEffect(() => {
    if (userInfo?.cpf || userInfo?.id || user) {
      const userKey = userInfo?.cpf || userInfo?.id || user || "default";
      const seen = localStorage.getItem(`spm_onboarding_tour_seen_${userKey}`);
      if (!seen) {
        const timer = setTimeout(() => {
          setTutorialAnchorEl(userHeaderRef.current);
          setShowTutorial(true);
          setTutorialStep(1);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [userInfo?.cpf, userInfo?.id, user]);

  const handleNextStep = () => {
    setTutorialStep((prev) => {
      const next = prev + 1;
      updateTutorialAnchor(next);
      return next;
    });
  };

  const handlePrevStep = () => {
    setTutorialStep((prev) => {
      const next = prev - 1;
      updateTutorialAnchor(next);
      return next;
    });
  };

  const handleDismissTutorial = () => {
    const userKey = userInfo?.cpf || userInfo?.id || user || "default";
    localStorage.setItem(`spm_onboarding_tour_seen_${userKey}`, "true");
    setShowTutorial(false);
    setTutorialAnchorEl(null);
    setTutorialStep(1);
  };

  const getInitials = (name: string) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleOpenCompanyMenu = (event: React.MouseEvent<HTMLElement>) => {
    setCompanyAnchorEl(event.currentTarget);
  };
  const handleCloseCompanyMenu = () => setCompanyAnchorEl(null);
  const isCompanyMenuOpen = Boolean(companyAnchorEl);

  const handleOpenRoleMenu = (event: React.MouseEvent<HTMLElement>) => {
    if ((currentCompany?.perfis || []).length > 1) {
      setRoleAnchorEl(event.currentTarget);
    }
  };
  const handleCloseRoleMenu = () => setRoleAnchorEl(null);
  const isRoleMenuOpen = Boolean(roleAnchorEl);

  const handleOpenDependenteMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (hasDependentes) {
      setDependenteAnchorEl(event.currentTarget);
    }
  };
  const handleCloseDependenteMenu = () => setDependenteAnchorEl(null);
  const isDependenteMenuOpen = Boolean(dependenteAnchorEl);

  const changeCurrentCompany = useCallback(
    async (company: any) => {
      await dispatch(loginActions.changeCurrentCompany(company));
      const callbackPath = getCallbackPath(currentLocation.pathname);
      if (callbackPath !== "") {
        navigate(callbackPath);
      }
    },
    [dispatch, currentLocation.pathname, navigate]
  );

  const changeCurrentProfile = useCallback(
    async (profile: any) => {
      await dispatch(loginActions.changeCurrentProfile(profile));
      const callbackPath = getCallbackPathFromRole(profile);
      navigate(callbackPath);
    },
    [dispatch, navigate]
  );

  const changeCurrentDependente = useCallback(
    async (dependente: any) => {
      if (!dependente.confirmarEmail) {
        const userData: UserDataTypesDependente = mountUserDataPostLoginDependentes(
          dependente.cpf,
          dependente,
          dependentes
        );
        await dispatch(loginActions.loginMenuLateral(userData));
        navigate(Paths.CollaboratorInitial);
      } else {
        setcpfDependenteAtual(dependente.cpf);
        setShowEmailModal(true);
      }
    },
    [dispatch, dependentes, navigate]
  );

  const avatarContent = (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <Avatar
        src={photoUrl || undefined}
        sx={{
          width: isCollapsed ? 38 : 42,
          height: isCollapsed ? 38 : 42,
          bgcolor: "#FF8310",
          color: "white",
          fontSize: isCollapsed ? 14 : 15,
          fontWeight: "bold",
          border: "2px solid rgba(255, 255, 255, 0.95)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}
      >
        {!photoUrl && getInitials(displayName)}
      </Avatar>
      <Box
        sx={{
          position: "absolute",
          bottom: 1,
          right: 1,
          width: 9,
          height: 9,
          borderRadius: "50%",
          bgcolor: "#10B981",
          border: "2px solid #0A5190",
        }}
      />
    </Box>
  );

  if (isCollapsed) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {displayName}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: "#60A5FA" }}>
                Perfil: {currentRoleDesc}
              </Typography>
              {currentCompany?.nome && (
                <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.8)" }}>
                  Empresa: {currentCompany.nome}
                </Typography>
              )}
              {hasDependentes && currentDependente?.nome && (
                <Typography variant="caption" sx={{ display: "block", color: "#A7F3D0" }}>
                  Dependente: {currentDependente.nome}
                </Typography>
              )}
            </Box>
          }
          placement="right"
          arrow
        >
          <Box onClick={handleOpenCompanyMenu} sx={{ cursor: "pointer" }}>
            {avatarContent}
          </Box>
        </Tooltip>

        {/* Collapsed Company/Profile/Dependente Menu */}
        <Popover
          open={isCompanyMenuOpen}
          anchorEl={companyAnchorEl}
          onClose={handleCloseCompanyMenu}
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
          PaperProps={{
            sx: {
              width: 250,
              p: 1,
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              maxHeight: 400,
              overflowY: "auto",
            },
          }}
        >
          {userCompanies && userCompanies.length > 0 && (
            <>
              <Typography variant="caption" sx={{ px: 1.5, pt: 1, pb: 0.5, color: "#6B7280", fontWeight: 700, display: "block" }}>
                ALTERAR EMPRESA
              </Typography>
              {userCompanies?.map((c: any) => (
                <MenuItem
                  key={c.id}
                  selected={c.id === currentCompany?.id}
                  onClick={() => {
                    handleCloseCompanyMenu();
                    if (c.id !== currentCompany?.id) {
                      setCompanyToChange(c);
                      setShowCompanyModal(true);
                    }
                  }}
                >
                  <ListItemIcon>
                    <BusinessIcon fontSize="small" sx={{ color: c.id === currentCompany?.id ? "#0A5190" : "#9CA3AF" }} />
                  </ListItemIcon>
                  <ListItemText primary={c.nome} primaryTypographyProps={{ fontSize: 13, fontWeight: c.id === currentCompany?.id ? 700 : 500 }} />
                  {c.id === currentCompany?.id && <CheckIcon fontSize="small" sx={{ color: "#0A5190" }} />}
                </MenuItem>
              ))}
            </>
          )}

          {hasDependentes && (
            <>
              <Typography variant="caption" sx={{ px: 1.5, pt: 1.5, pb: 0.5, color: "#6B7280", fontWeight: 700, display: "block" }}>
                ALTERAR DEPENDENTE
              </Typography>
              {dependentes?.map((d: any) => {
                const isSelected = d.cpf === currentDependente?.cpf || d.nome === currentDependente?.nome;
                return (
                  <MenuItem
                    key={d.cpf || d.nome}
                    selected={isSelected}
                    onClick={() => {
                      handleCloseCompanyMenu();
                      if (!isSelected) {
                        setDependenteToChange(d);
                        setShowDependenteModal(true);
                      }
                    }}
                  >
                    <ListItemIcon>
                      <FamilyRestroomIcon fontSize="small" sx={{ color: isSelected ? "#0A5190" : "#9CA3AF" }} />
                    </ListItemIcon>
                    <ListItemText primary={d.nome} primaryTypographyProps={{ fontSize: 13, fontWeight: isSelected ? 700 : 500 }} />
                    {isSelected && <CheckIcon fontSize="small" sx={{ color: "#0A5190" }} />}
                  </MenuItem>
                );
              })}
            </>
          )}
        </Popover>
      </Box>
    );
  }

  const perfis = currentCompany?.perfis || [];

  return (
    <Box sx={{ px: 1.5, py: 1, position: "relative", zIndex: showTutorial ? 1260 : "auto" }}>
      <Box
        sx={{
          background: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 100%)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.18)",
          borderRadius: "14px",
          p: 1.2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
        }}
      >
        {/* User Header with setinha on the far right for Dependente switch */}
        <Tooltip
          title={hasDependentes ? "Clique para alterar o dependente" : ""}
          placement="top"
          arrow
          disableHoverListener={!hasDependentes}
        >
          <Box
            ref={userHeaderRef}
            onClick={hasDependentes ? handleOpenDependenteMenu : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              p: 0.6,
              mx: -0.4,
              borderRadius: "10px",
              cursor: hasDependentes ? "pointer" : "default",
              transition: "all 0.3s ease",
              position: "relative",
              zIndex: showTutorial && tutorialStep === (hasDependentes ? 3 : 99) ? 1261 : 1,
              border: showTutorial && tutorialStep === (hasDependentes ? 3 : 99) ? "1.5px solid #FF8310" : "1.5px solid transparent",
              bgcolor: showTutorial && tutorialStep === (hasDependentes ? 3 : 99) ? "rgba(10, 81, 144, 0.95)" : "transparent",
              ...(showTutorial && tutorialStep === (hasDependentes ? 3 : 99)
                ? {
                    "@keyframes pulseAmberHeader": {
                      "0%": {
                        boxShadow: "0 0 0 0px rgba(255, 131, 16, 0.8), 0 0 10px rgba(255, 131, 16, 0.6)",
                      },
                      "50%": {
                        boxShadow: "0 0 0 6px rgba(255, 131, 16, 0.35), 0 0 22px rgba(255, 131, 16, 0.9)",
                      },
                      "100%": {
                        boxShadow: "0 0 0 0px rgba(255, 131, 16, 0.8), 0 0 10px rgba(255, 131, 16, 0.6)",
                      },
                    },
                    animation: "pulseAmberHeader 1.6s infinite ease-in-out",
                  }
                : {}),
              "&:hover": hasDependentes
                ? {
                    bgcolor: "rgba(255, 255, 255, 0.12)",
                  }
                : {},
            }}
          >
            {/* Left side: Avatar + User Name & Role Status */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0, flex: 1 }}>
              {avatarContent}
              <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    fontSize: "13.5px",
                    lineHeight: 1.2,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {displayName}
                </Typography>
                {hasDependentes && (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      fontSize: "10.5px",
                      fontWeight: 600,
                      display: "block",
                      lineHeight: 1.1,
                      mt: 0.2,
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    {currentDependente?.responsavel ? "Titular (Responsável)" : "Dependente"}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Right side: Arrow indicator or Loading Spinner */}
            {hasDependentes && (
              isLoadingDependente ? (
                <CircularProgress size={16} sx={{ color: "white", flexShrink: 0 }} />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 18,
                    color: "rgba(255, 255, 255, 0.8)",
                    transform: isDependenteMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                    flexShrink: 0,
                  }}
                />
              )
            )}
          </Box>
        </Tooltip>

        {/* 1. SELETOR DE EMPRESA */}
        <Box
          ref={companyBoxRef}
          onClick={handleOpenCompanyMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 0.7,
            borderRadius: "8px",
            position: "relative",
            zIndex: showTutorial && tutorialStep === 1 ? 1261 : 1,
            bgcolor: showTutorial && tutorialStep === 1 ? "rgba(10, 81, 144, 0.95)" : "rgba(0, 0, 0, 0.22)",
            border: showTutorial && tutorialStep === 1 ? "1.5px solid #FF8310" : "1px solid rgba(255, 255, 255, 0.12)",
            cursor: "pointer",
            transition: "all 0.3s ease",
            ...(showTutorial && tutorialStep === 1
              ? {
                  "@keyframes pulseAmberCompany": {
                    "0%": {
                      boxShadow: "0 0 0 0px rgba(255, 131, 16, 0.8), 0 0 10px rgba(255, 131, 16, 0.6)",
                    },
                    "50%": {
                      boxShadow: "0 0 0 6px rgba(255, 131, 16, 0.35), 0 0 22px rgba(255, 131, 16, 0.9)",
                    },
                    "100%": {
                      boxShadow: "0 0 0 0px rgba(255, 131, 16, 0.8), 0 0 10px rgba(255, 131, 16, 0.6)",
                    },
                  },
                  animation: "pulseAmberCompany 1.6s infinite ease-in-out",
                }
              : {}),
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.38)",
              borderColor: "rgba(255, 255, 255, 0.3)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0, flex: 1 }}>
            <BusinessIcon sx={{ fontSize: 15, color: "#FF8310" }} />
            <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "9px", display: "block", lineHeight: 1, textTransform: "uppercase", fontWeight: 700 }}>
                Empresa
              </Typography>
              <Typography variant="subtitle2" noWrap sx={{ color: "white", fontSize: "11.5px", fontWeight: 600, lineHeight: 1.2, fontFamily: "Inter, sans-serif" }}>
                {currentCompany?.nome || "Selecionar Empresa"}
              </Typography>
            </Box>
          </Box>
          {isLoadingCompany ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : (
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 16,
                color: "rgba(255, 255, 255, 0.7)",
                transform: isCompanyMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          )}
        </Box>

        {/* 2. SELETOR DE PERFIL */}
        <Box
          ref={roleBoxRef}
          onClick={handleOpenRoleMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 0.7,
            borderRadius: "8px",
            position: "relative",
            zIndex: showTutorial && tutorialStep === 2 ? 1261 : 1,
            bgcolor: showTutorial && tutorialStep === 2 ? "rgba(10, 81, 144, 0.95)" : "rgba(0, 0, 0, 0.22)",
            border: showTutorial && tutorialStep === 2 ? "1.5px solid #60A5FA" : "1px solid rgba(255, 255, 255, 0.12)",
            cursor: perfis.length > 1 ? "pointer" : "default",
            transition: "all 0.3s ease",
            ...(showTutorial && tutorialStep === 2
              ? {
                  "@keyframes pulseBlueRole": {
                    "0%": {
                      boxShadow: "0 0 0 0px rgba(96, 165, 250, 0.8), 0 0 10px rgba(96, 165, 250, 0.6)",
                    },
                    "50%": {
                      boxShadow: "0 0 0 6px rgba(96, 165, 250, 0.35), 0 0 22px rgba(96, 165, 250, 0.9)",
                    },
                    "100%": {
                      boxShadow: "0 0 0 0px rgba(96, 165, 250, 0.8), 0 0 10px rgba(96, 165, 250, 0.6)",
                    },
                  },
                  animation: "pulseBlueRole 1.6s infinite ease-in-out",
                }
              : {}),
            "&:hover": perfis.length > 1 ? {
              bgcolor: "rgba(0, 0, 0, 0.38)",
              borderColor: "rgba(255, 255, 255, 0.3)",
            } : {},
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0, flex: 1 }}>
            <BadgeIcon sx={{ fontSize: 15, color: "#60A5FA" }} />
            <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "9px", display: "block", lineHeight: 1, textTransform: "uppercase", fontWeight: 700 }}>
                Perfil
              </Typography>
              <Typography variant="subtitle2" noWrap sx={{ color: "#93C5FD", fontSize: "11.5px", fontWeight: 600, lineHeight: 1.2, fontFamily: "Inter, sans-serif" }}>
                {currentRoleDesc}
              </Typography>
            </Box>
          </Box>
          {perfis.length > 1 && (
            isLoadingRole ? (
              <CircularProgress size={14} sx={{ color: "white" }} />
            ) : (
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 16,
                  color: "rgba(255, 255, 255, 0.7)",
                  transform: isRoleMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            )
          )}
        </Box>
      </Box>

      {/* Popover Empresa */}
      <Popover
        open={isCompanyMenuOpen}
        anchorEl={companyAnchorEl}
        onClose={handleCloseCompanyMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: 250,
            mt: 0.8,
            p: 1,
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            border: "1px solid #E5E7EB",
          },
        }}
      >
        <Typography variant="caption" sx={{ px: 1.5, pt: 1, pb: 0.5, color: "#9CA3AF", fontWeight: 700, display: "block" }}>
          ALTERAR EMPRESA
        </Typography>
        {userCompanies?.map((c: any) => (
          <MenuItem
            key={c.id}
            selected={c.id === currentCompany?.id}
            onClick={() => {
              handleCloseCompanyMenu();
              if (c.id !== currentCompany?.id) {
                setCompanyToChange(c);
                setShowCompanyModal(true);
              }
            }}
            sx={{ borderRadius: "8px", my: 0.3 }}
          >
            <ListItemIcon>
              <BusinessIcon fontSize="small" sx={{ color: c.id === currentCompany?.id ? "#0A5190" : "#9CA3AF" }} />
            </ListItemIcon>
            <ListItemText primary={c.nome} primaryTypographyProps={{ fontSize: 13, fontWeight: c.id === currentCompany?.id ? 700 : 500 }} />
            {c.id === currentCompany?.id && <CheckIcon fontSize="small" sx={{ color: "#0A5190" }} />}
          </MenuItem>
        ))}
      </Popover>

      {/* Popover Perfil */}
      <Popover
        open={isRoleMenuOpen}
        anchorEl={roleAnchorEl}
        onClose={handleCloseRoleMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: 250,
            mt: 0.8,
            p: 1,
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            border: "1px solid #E5E7EB",
          },
        }}
      >
        <Typography variant="caption" sx={{ px: 1.5, pt: 1, pb: 0.5, color: "#9CA3AF", fontWeight: 700, display: "block" }}>
          ALTERAR PERFIL
        </Typography>
        {perfis.map((p: any) => (
          <MenuItem
            key={p}
            selected={p === currentProfile}
            onClick={() => {
              handleCloseRoleMenu();
              if (p !== currentProfile) {
                setRoleToChange(p);
                setShowRoleModal(true);
              }
            }}
            sx={{ borderRadius: "8px", my: 0.3 }}
          >
            <ListItemIcon>
              <BadgeIcon fontSize="small" sx={{ color: p === currentProfile ? "#0A5190" : "#9CA3AF" }} />
            </ListItemIcon>
            <ListItemText primary={getDescricaoByCodigo(p)} primaryTypographyProps={{ fontSize: 13, fontWeight: p === currentProfile ? 700 : 500 }} />
            {p === currentProfile && <CheckIcon fontSize="small" sx={{ color: "#0A5190" }} />}
          </MenuItem>
        ))}
      </Popover>

      {/* Popover Dependente */}
      <Popover
        open={isDependenteMenuOpen}
        anchorEl={dependenteAnchorEl}
        onClose={handleCloseDependenteMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: 250,
            mt: 0.8,
            p: 1,
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            border: "1px solid #E5E7EB",
            maxHeight: 300,
            overflowY: "auto",
          },
        }}
      >
        <Typography variant="caption" sx={{ px: 1.5, pt: 1, pb: 0.5, color: "#9CA3AF", fontWeight: 700, display: "block" }}>
          ALTERAR DEPENDENTE
        </Typography>
        {dependentes?.map((d: any) => {
          const isSelected = d.cpf === currentDependente?.cpf || d.nome === currentDependente?.nome;
          return (
            <MenuItem
              key={d.cpf || d.nome}
              selected={isSelected}
              onClick={() => {
                handleCloseDependenteMenu();
                if (!isSelected) {
                  setDependenteToChange(d);
                  setShowDependenteModal(true);
                }
              }}
              sx={{ borderRadius: "8px", my: 0.3 }}
            >
              <ListItemIcon>
                <FamilyRestroomIcon fontSize="small" sx={{ color: isSelected ? "#0A5190" : "#9CA3AF" }} />
              </ListItemIcon>
              <ListItemText primary={d.nome} primaryTypographyProps={{ fontSize: 13, fontWeight: isSelected ? 700 : 500 }} />
              {isSelected && <CheckIcon fontSize="small" sx={{ color: "#0A5190" }} />}
            </MenuItem>
          );
        })}
      </Popover>

      {/* Confirmation Modals */}
      <GenericModal
        visible={showCompanyModal}
        isLoading={isLoadingCompany}
        onCancel={async () => {
          if (companyToChange?.nome) {
            try {
              setIsLoadingCompany(true);
              localStorage.setItem("currentCompany", JSON.stringify(companyToChange));
              localStorage.setItem("currentProfile", companyToChange.perfis[0]);
              await changeCurrentCompany(companyToChange);
            } catch (err) {
              console.error("Erro ao alterar empresa:", err);
            } finally {
              setIsLoadingCompany(false);
              setShowCompanyModal(false);
            }
          }
        }}
        onConfirm={() => {
          if (!isLoadingCompany) setShowCompanyModal(false);
        }}
        modalTitle={`Tem certeza que deseja logar na Empresa ${companyToChange?.nome}?`}
        modalSubtitle={"Dados não preenchidos podem ser perdidos"}
        cancelButtonMsg={"Cancelar"}
        confirmButtonMsg={"Confirmar"}
      />

      <GenericModal
        visible={showRoleModal}
        isLoading={isLoadingRole}
        onCancel={async () => {
          if (roleToChange) {
            try {
              setIsLoadingRole(true);
              localStorage.setItem("currentProfile", roleToChange);
              await changeCurrentProfile(roleToChange);
            } catch (err) {
              console.error("Erro ao alterar perfil:", err);
            } finally {
              setIsLoadingRole(false);
              setShowRoleModal(false);
            }
          }
        }}
        onConfirm={() => {
          if (!isLoadingRole) setShowRoleModal(false);
        }}
        modalTitle={`Tem certeza que deseja ir para o Perfil ${getDescricaoByCodigo(roleToChange)}?`}
        modalSubtitle={""}
        cancelButtonMsg={"Cancelar"}
        confirmButtonMsg={"Confirmar"}
      />

      <GenericModal
        visible={showDependenteModal}
        isLoading={isLoadingDependente}
        onCancel={async () => {
          if (dependenteToChange) {
            try {
              setIsLoadingDependente(true);
              await changeCurrentDependente(dependenteToChange);
            } catch (err) {
              console.error("Erro ao alterar dependente:", err);
            } finally {
              setIsLoadingDependente(false);
              setShowDependenteModal(false);
            }
          }
        }}
        onConfirm={() => {
          if (!isLoadingDependente) setShowDependenteModal(false);
        }}
        modalTitle={`Tem certeza que deseja ir para o Perfil do dependente: ${dependenteToChange ? dependenteToChange.nome : ''}?`}
        modalSubtitle={""}
        cancelButtonMsg={"Cancelar"}
        confirmButtonMsg={"Confirmar"}
      />

      <EmailModal
        visible={showEmailModal}
        user={cpfDependenteAtual}
        onConfirm={() => setShowEmailModal(false)}
        modalTitle={`Solicitação de e-mail`}
        modalSubtitle={'Preencha o campo abaixo com o e-mail do dependente maior de idade, para que ele possa fazer o login e gerenciar suas próprias consultas.'}
        cancelButtonMsg={'Cancelar'}
        confirmButtonMsg={'Confirmar'}
      />

      {/* SVG Mask Cutout Overlay & Pulsing Border */}
      {showTutorial && rect && (
        <Portal>
          {/* Fullscreen SVG with Cutout Hole over the target element */}
          <svg
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 1250,
              pointerEvents: "none",
            }}
          >
            <defs>
              <mask id="spm-tutorial-cutout-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <rect
                  x={rect.left - 4}
                  y={rect.top - 4}
                  width={rect.width + 8}
                  height={rect.height + 8}
                  rx={10}
                  ry={10}
                  fill="black"
                />
              </mask>
            </defs>

            {/* Dark Backdrop Mask with Cutout Hole */}
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0, 0, 0, 0.72)"
              mask="url(#spm-tutorial-cutout-mask)"
              style={{ pointerEvents: "auto", cursor: "pointer" }}
              onClick={handleDismissTutorial}
            />
          </svg>

          {/* Glowing Animated Pulsing Border Overlay around the Cutout Window */}
          <Box
            sx={{
              position: "fixed",
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
              borderRadius: "10px",
              pointerEvents: "none",
              zIndex: 1251,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              border: tutorialStep === 1 ? "2px solid #10B981" : tutorialStep === 3 ? "2px solid #60A5FA" : "2px solid #FF8310",
              ...(tutorialStep === 1
                ? {
                    "@keyframes pulseGreenPortal": {
                      "0%": {
                        boxShadow: "0 0 0 0px rgba(16, 185, 129, 0.8), 0 0 12px rgba(16, 185, 129, 0.8)",
                      },
                      "50%": {
                        boxShadow: "0 0 0 8px rgba(16, 185, 129, 0.35), 0 0 24px rgba(16, 185, 129, 0.9)",
                      },
                      "100%": {
                        boxShadow: "0 0 0 0px rgba(16, 185, 129, 0.8), 0 0 12px rgba(16, 185, 129, 0.8)",
                      },
                    },
                    animation: "pulseGreenPortal 1.6s infinite ease-in-out",
                  }
                : tutorialStep === 3
                ? {
                    "@keyframes pulseBluePortal": {
                      "0%": {
                        boxShadow: "0 0 0 0px rgba(96, 165, 250, 0.8), 0 0 12px rgba(96, 165, 250, 0.8)",
                      },
                      "50%": {
                        boxShadow: "0 0 0 8px rgba(96, 165, 250, 0.35), 0 0 24px rgba(96, 165, 250, 0.9)",
                      },
                      "100%": {
                        boxShadow: "0 0 0 0px rgba(96, 165, 250, 0.8), 0 0 12px rgba(96, 165, 250, 0.8)",
                      },
                    },
                    animation: "pulseBluePortal 1.6s infinite ease-in-out",
                  }
                : {
                    "@keyframes pulseAmberPortal": {
                      "0%": {
                        boxShadow: "0 0 0 0px rgba(255, 131, 16, 0.8), 0 0 12px rgba(255, 131, 16, 0.8)",
                      },
                      "50%": {
                        boxShadow: "0 0 0 8px rgba(255, 131, 16, 0.35), 0 0 24px rgba(255, 131, 16, 0.9)",
                      },
                      "100%": {
                        boxShadow: "0 0 0 0px rgba(255, 131, 16, 0.8), 0 0 12px rgba(255, 131, 16, 0.8)",
                      },
                    },
                    animation: "pulseAmberPortal 1.6s infinite ease-in-out",
                  }),
            }}
          />
        </Portal>
      )}

      {/* Step-by-Step Guided Tour Popover */}
      <Popover
        open={showTutorial && Boolean(tutorialAnchorEl)}
        anchorEl={tutorialAnchorEl}
        onClose={handleDismissTutorial}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            ml: 1.5,
            width: 320,
            p: 2.2,
            borderRadius: "16px",
            background: "linear-gradient(135deg, #0A5190 0%, #063966 100%)",
            color: "white",
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.5)",
            border: "1px solid rgba(255, 255, 255, 0.25)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            zIndex: 1300,
          },
        }}
      >
        {/* Stepper Header: Step Badge + Stepper Dots */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Chip
            label={
              tutorialStep === 1
                ? "BEM-VINDO!"
                : `NOVIDADE ${tutorialStep - 1} DE ${totalSteps - 1}`
            }
            size="small"
            sx={{
              bgcolor: tutorialStep === 1 ? "#10B981" : tutorialStep === 3 ? "#3B82F6" : "#FF8310",
              color: "white",
              fontWeight: 800,
              fontSize: "10px",
              height: 22,
              borderRadius: "6px",
              fontFamily: "Inter, sans-serif",
            }}
          />
          {/* Stepper Dots */}
          <Box sx={{ display: "flex", gap: 0.6, alignItems: "center" }}>
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  width: tutorialStep === idx + 1 ? 16 : 7,
                  height: 7,
                  borderRadius: 4,
                  bgcolor: tutorialStep === idx + 1 ? "#FF8310" : "rgba(255,255,255,0.4)",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Step Content */}
        {tutorialStep === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Box
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  p: 0.9,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AutoAwesomeIcon sx={{ color: "#FBBF24", fontSize: 22 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: "14.5px", color: "#FFFFFF" }}>
                Bem-vindo ao Saúde Plena!
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: "12.5px", color: "rgba(255, 255, 255, 0.9)", lineHeight: 1.45, mt: 0.5 }}>
              Preparamos novidades na barra lateral para tornar sua navegação mais rápida e simples. Gostaria de fazer um tour rápido para conhecer as novidades?
            </Typography>
          </Box>
        )}

        {tutorialStep === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Box
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  p: 0.9,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BusinessIcon sx={{ color: "#FF8310", fontSize: 22 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: "14.5px", color: "#FFFFFF" }}>
                Novidade: Seletor de Empresa
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: "12.5px", color: "rgba(255, 255, 255, 0.9)", lineHeight: 1.45, mt: 0.5 }}>
              Se a sua conta possui acesso a mais de uma empresa ou unidade, você pode alternar o seu ambiente de trabalho clicando neste seletor.
            </Typography>
          </Box>
        )}

        {tutorialStep === 3 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Box
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  p: 0.9,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BadgeIcon sx={{ color: "#60A5FA", fontSize: 22 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: "14.5px", color: "#FFFFFF" }}>
                Novidade: Seletor de Perfil
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: "12.5px", color: "rgba(255, 255, 255, 0.9)", lineHeight: 1.45, mt: 0.5 }}>
              Alterne seu perfil de atuação (ex: Colaborador, Gestor, Médico) para visualizar diferentes permissões e menus na plataforma.
            </Typography>
          </Box>
        )}

        {tutorialStep === 4 && hasDependentes && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Box
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  p: 0.9,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FamilyRestroomIcon sx={{ color: "#FF8310", fontSize: 22 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: "14.5px", color: "#FFFFFF" }}>
                Novidade: Troca pro Dependente
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: "12.5px", color: "rgba(255, 255, 255, 0.9)", lineHeight: 1.45, mt: 0.5 }}>
              Você possui dependentes cadastrados! Clique no seu nome no topo da barra lateral a qualquer momento para abrir a lista e alternar a visão para o seu dependente.
            </Typography>
          </Box>
        )}

        {/* Stepper Footer Action Buttons */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2.2, pt: 1, borderTop: "1px solid rgba(255,255,255,0.15)" }}>
          {tutorialStep === 1 ? (
            <Button
              onClick={handleDismissTutorial}
              size="small"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "none",
                p: 0.5,
                "&:hover": { color: "white", bgcolor: "transparent" },
              }}
            >
              Pular tour
            </Button>
          ) : (
            <Button
              onClick={handlePrevStep}
              size="small"
              sx={{
                color: "white",
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "none",
                p: 0.5,
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              ← Voltar
            </Button>
          )}

          {tutorialStep === 1 ? (
            <Button
              onClick={handleNextStep}
              variant="contained"
              size="small"
              sx={{
                bgcolor: "#FF8310",
                color: "white",
                fontWeight: 700,
                fontSize: "11.5px",
                borderRadius: "8px",
                textTransform: "none",
                px: 2,
                py: 0.5,
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                "&:hover": {
                  bgcolor: "#E06F00",
                },
              }}
            >
              Iniciar Tour →
            </Button>
          ) : tutorialStep < totalSteps ? (
            <Button
              onClick={handleNextStep}
              variant="contained"
              size="small"
              sx={{
                bgcolor: "#FF8310",
                color: "white",
                fontWeight: 700,
                fontSize: "11.5px",
                borderRadius: "8px",
                textTransform: "none",
                px: 2,
                py: 0.5,
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                "&:hover": {
                  bgcolor: "#E06F00",
                },
              }}
            >
              Próximo →
            </Button>
          ) : (
            <Button
              onClick={handleDismissTutorial}
              variant="contained"
              size="small"
              sx={{
                bgcolor: "#10B981",
                color: "white",
                fontWeight: 700,
                fontSize: "11.5px",
                borderRadius: "8px",
                textTransform: "none",
                px: 2,
                py: 0.5,
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                "&:hover": {
                  bgcolor: "#059669",
                },
              }}
            >
              Concluir ✓
            </Button>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default UserProfile;
